import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import {
  getAssociationsAll,
  getAssociationsEntities,
} from '@collections/associations/associations.selectors';
import { CandidateSelectedObj, getCandidatesEntities } from '@collections/candidates';
import { hasAnonymization } from '@collections/users';
import { loadLicenseExtensions } from '@core/settings/license-configuration';
import { getJobPageCandidatesState } from '@modules/job-page/store/job.selectors';
import {
  Association,
  AssociationStatusConfiguration,
  AssociationStatusConfigurationTypes,
  CandidateAdapter,
  CandidateWithAssociation,
  DELETED_CANDIDATE,
  Candidate,
} from '@shared/models';
import { Pagination, PaginationAdapter } from '@shared/pagination';
import { isTruthy } from '@shared/utils';

import {
  getJobPageState,
  JobPageState,
  DEFAULT_JOB_ID,
  statusConfigurationsAdapter,
} from '../../job-page/store/job.reducer';
import { AssignsState } from './assigns.reducer';

interface AssociationConfigurationSelectorOptions {
  jobId: number | string;
  jobIds: (number | string)[];
  selectedPhaseId: string;
  limit;
  number;
  phaseId: string;
  stateId: string;
  configurationStateId: string;
  type: AssociationStatusConfigurationTypes;
  isGreenhouseAts: boolean;
  currentAssociation: Association;
  moveForwardOnly: boolean;
}

export interface SelectedPhaseObj {
  status: string;
  subStatus: string;
}
export const getState = createFeatureSelector<AssignsState>('candidatePage');

export const getAssignsCandidatesState: MemoizedSelector<
  JobPageState,
  Pagination<Candidate>
> = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.shortlistedCandidates,
);

export const getAssignsCandidatesNotLoaded = createSelector(
  getAssignsCandidatesState,
  (state: any) => (!!state ? state.loading : true),
);

export const getShortlistTotalElements = createSelector(getJobPageState, (state: JobPageState) =>
  !!state.shortlistedCandidates ? state.shortlistedCandidates.totalElements : 0,
);

export const getAssignsAssociationsState = createSelector(
  getState,
  (state: AssignsState) => !!state && state.associations,
);
export const getAssignsCompletedState = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.assignsCompleted,
);

export const getAssignsCandidatesIds = createSelector(
  getAssignsCandidatesState,
  (pagination: Pagination) => !!pagination && new PaginationAdapter(pagination).contentIds,
);

export const getAssignsRejectedCandidatesPagination = createSelector(
  getJobPageState,
  (state: JobPageState) => !!state && state.rejectedCandidates.paginationList,
);

export const getAssignsRejectedCandidatesEntities = createSelector(
  getJobPageState,
  (state: JobPageState) =>
    !!state.rejectedCandidates.paginationList && state.rejectedCandidates.paginationList.content,
);

export const getAssignsRejectedCandidatesTotalNumber = createSelector(
  getJobPageState,
  (state: JobPageState) =>
    !!state.rejectedCandidates.paginationList
      ? state.rejectedCandidates.paginationList.totalElements
      : 0,
);

export const getAssignsRejectedCandidatesIsLoading = createSelector(
  getJobPageState,
  (state: JobPageState) =>
    !!state && !!state.rejectedCandidates && state.rejectedCandidates.isLoading,
);

export const getAssignsRejectedCandidates = createSelector(
  getAssignsRejectedCandidatesEntities,
  hasAnonymization,
  (entities, anonymization) => {
    if (!!entities) {
      return entities.map((candidate) => new CandidateAdapter(candidate, anonymization));
    }
  },
);

export const getAssignsCandidates = createSelector(
  getAssignsCandidatesIds,
  getCandidatesEntities,
  hasAnonymization,
  (ids, entities, anonymization) =>
    !!ids && ids.map((id: number) => new CandidateAdapter(entities[id], anonymization)),
);
export const getAssignsAssociationsIds = createSelector(
  getAssignsAssociationsState,
  (pagination: Pagination) => !!pagination && new PaginationAdapter(pagination).contentIds,
);
export const getAssignsAssociationsTotal = createSelector(
  getAssignsAssociationsState,
  (pagination: Pagination) => (pagination?.loading ? 0 : pagination?.totalElements),
);

export const getAssignsAssociations = createSelector(
  getAssignsAssociationsIds,
  getAssociationsEntities,
  (ids, entities) => (!!ids ? ids.map((id: number) => !!entities && entities[id]) : []),
);
export const getAssignsWithCandidates = createSelector(
  getAssociationsAll,
  getAssignsCandidates,
  (associations, entities) =>
    !!entities &&
    !!associations &&
    entities
      .map((candidate: CandidateAdapter) => {
        const association = associations.find(
          (a: Association) => !!a && a.candidateId === candidate.id,
        );
        return { candidate, association } as CandidateWithAssociation;
      })
      .filter(isTruthy),
);

export const getAssignsWithRejectedCandidates = createSelector(
  getAssociationsAll,
  getAssignsRejectedCandidates,
  (associations, rejectedEntities) =>
    !!rejectedEntities &&
    !!associations &&
    rejectedEntities
      .map((rejectedCandidate: CandidateAdapter) => {
        const association = associations.find(
          (a: Association) => !!a && a.candidateId === rejectedCandidate.id,
        );

        return {
          candidate: rejectedCandidate,
          association,
        } as CandidateWithAssociation;
      })
      .filter(isTruthy),
);

export const getAssignsWithSelectedCandidate = createSelector(
  getAssignsAssociations,
  CandidateSelectedObj.getCandidatesSelected(),
  (associations, candidate) =>
    !!associations &&
    candidate.status !== DELETED_CANDIDATE &&
    associations
      .filter((association) => association.candidateId === candidate.id)
      .map((association: Association) => ({
        ...association,
        candidate,
      })),
);

export const areAssignsCompleted = createSelector(
  getAssignsCompletedState,
  (state: { fired: number }) => state.fired === 0,
);
export const getFiltersSetOnAssignedCandidates = createSelector(
  getJobPageState,
  (state: JobPageState) => state?.shortlistedFilters.filters,
);

export const getHMFiltersSetOnAssignedCandidates = createSelector(
  getJobPageState,
  (state: JobPageState) => state?.shortlistedFilters.hmSelectedStatuses,
);

export const getCandidateAssociatedJobsIds = createSelector(
  getAssociationsEntities,
  (associations, { candidateId }) => {
    const associationArray: Association[] = Object.values(associations);
    return (
      !!associations &&
      Object.keys(associations).length > 0 &&
      associationArray
        .filter((association) => association.candidateId.toString() === candidateId.toString())
        .map((association) => association.reqId)
    );
  },
);

const { selectAll: selectAllAssociationConfigurations } =
  statusConfigurationsAdapter.getSelectors();

export const getAssociationStatusConfigurationsState = createSelector(
  getJobPageState,
  (state) => state.statusConfigurations,
);

export const getAssociationStatusConfigurations = createSelector(
  getAssociationStatusConfigurationsState,
  loadLicenseExtensions,
  (
    state,
    extensions,
    { jobId = DEFAULT_JOB_ID }: Partial<AssociationConfigurationSelectorOptions>,
  ) => {
    if (
      !extensions.some(
        (extension) => extension === AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
      )
    ) {
      jobId = DEFAULT_JOB_ID;
    }
    return !!state && state[`${jobId}`] && state[`${jobId}`].ready
      ? selectAllAssociationConfigurations(state[`${jobId}`])
      : [];
  },
);

export const getAssociationStatusConfigurationsForJobs = createSelector(
  getAssociationStatusConfigurationsState,
  loadLicenseExtensions,
  (
    state,
    extensions,
    { jobIds = [DEFAULT_JOB_ID] }: Partial<AssociationConfigurationSelectorOptions>,
  ) => {
    if (
      !extensions.some(
        (extension) => extension === AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
      )
    ) {
      const configurations = getAssociationStatusConfigurationsShortListFn(
        selectAllAssociationConfigurations(state[DEFAULT_JOB_ID]),
      );
      return jobIds
        .map((key) => `${key}`)
        .reduce((object, key) => ({ ...object, [key]: configurations }), {});
    }
    return jobIds
      .map((key) => `${key}`)
      .reduce(
        (object, key) => ({
          ...object,
          [key]: selectAllAssociationConfigurations(state[key]),
        }),
        {},
      );
  },
);

export const getAssociationStatusConfigurationsReady = createSelector(
  getAssociationStatusConfigurationsState,
  loadLicenseExtensions,
  (
    state,
    extensions,
    { jobId = DEFAULT_JOB_ID }: Partial<AssociationConfigurationSelectorOptions>,
  ) => {
    if (
      !extensions.some(
        (extension) => extension === AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
      )
    ) {
      jobId = DEFAULT_JOB_ID;
    }
    return !!state && state[jobId] && state[jobId].ready ? state[jobId].ready : false;
  },
);

export const getAssociationStatusConfigurationsForJobsReady = createSelector(
  getAssociationStatusConfigurationsState,
  loadLicenseExtensions,
  (
    state,
    extensions,
    { jobIds = [DEFAULT_JOB_ID] }: Partial<AssociationConfigurationSelectorOptions>,
  ) => {
    if (
      !extensions.some(
        (extension) => extension === AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
      )
    ) {
      jobIds = [DEFAULT_JOB_ID];
    }
    return jobIds
      .map((key) => `${key}`)
      .every((jobId) => !!state && state[`${jobId}`] && state[`${jobId}`].ready);
  },
);

export const getFlattenAssociationStatusConfigurations = createSelector(
  getAssociationStatusConfigurations,
  (configurations) => configurations.flatMap(({ children, ...rest }) => [rest, ...children]),
);

export const getAssociationStatusConfigurationsShortList = createSelector(
  getAssociationStatusConfigurations,
  (phases) => getAssociationStatusConfigurationsShortListFn(phases),
);

export const getAssociationStatusConfigurationsRejected = createSelector(
  getAssociationStatusConfigurations,
  (phases) => getAssociationStatusConfigurationsRejectedFn(phases),
);

export const getAssociationStatusConfigurationsShortListFn = (
  phases: AssociationStatusConfiguration[],
) =>
  (phases || [])
    .filter((phase) => phase.isShortListState && !phase.isRejectedState)
    .map((phase) => ({
      ...phase,
      children: (phase.children || []).filter(
        (state) => state.isShortListState && !state.isRejectedState,
      ),
    }));

export const getAssociationStatusConfigurationsRejectedFn = (
  phases: AssociationStatusConfiguration[],
) => (phases || []).flatMap((phase) => phase.children).filter((state) => state.isRejectedState);

export const getInitialPhase = createSelector(
  getAssociationStatusConfigurationsShortList,
  (
    associationsConfiguration: AssociationStatusConfiguration[],
    { currentAssociation }: Partial<AssociationConfigurationSelectorOptions>,
  ) => getInitialPhaseFn(associationsConfiguration, !!currentAssociation && currentAssociation),
);

export const getInitialPhaseFn = (associationsConfiguration, currentAssociation) => {
  let phaseToReturn = {} as SelectedPhaseObj;

  if (associationsConfiguration && associationsConfiguration.length > 0) {
    const filteredPhase =
      associationsConfiguration.find(({ id }) => id === currentAssociation.status) ||
      associationsConfiguration[0];
    const filteredState =
      filteredPhase.children.find(({ id }) => id === currentAssociation.subStatus) ||
      (filteredPhase.children.length > 0 && filteredPhase.children[0]);

    phaseToReturn = { status: filteredPhase.id, subStatus: filteredState.id };
  }

  return phaseToReturn;
};

export const getInitialShortListedPhaseFn = (
  associationsConfiguration: AssociationStatusConfiguration[],
) => {
  const shortListedPhases = (associationsConfiguration || []).filter(
    (configuration) => configuration.isShortListState,
  );
  return shortListedPhases.length > 0
    ? shortListedPhases[0]
    : ({} as AssociationStatusConfiguration);
};

export const getMatchedCandidatePhase = createSelector(
  getAssociationStatusConfigurations,
  (phases) => {
    const phase =
      !!phases && phases.length > 0
        ? phases.find((phase) => !phase.isShortListState && !phase.isProspectState)
        : null;
    const state =
      !!phase &&
      !!phase.children &&
      phase.children.filter((childConfig) => childConfig.isShortListState).length > 0
        ? phase.children[0]
        : null;
    return { phase, state };
  },
);

export const getFinalPhase = createSelector(
  getAssociationStatusConfigurationsShortList,
  (associationsConfiguration) => associationsConfiguration[associationsConfiguration.length - 1],
);

const getAssociationPhaseIndex = (configurationStateId, statesConfigurations) =>
  statesConfigurations.findIndex(
    ({ id, children }) =>
      id === configurationStateId || children.some((state) => state.id === configurationStateId),
  );

export const getAssociationStatusMoveForwardSteps = createSelector(
  getAssociationStatusConfigurations,
  (statesConfigurations, { selectedPhaseId, limit }) => {
    const phaseIndex = getAssociationPhaseIndex(selectedPhaseId, statesConfigurations);
    const startIndex = Math.max(0, phaseIndex);

    if (typeof limit === 'number') {
      return statesConfigurations.slice(startIndex, startIndex + limit);
    }
    return statesConfigurations.slice(startIndex);
  },
);

export const isAssociationRejected = createSelector(
  getFlattenAssociationStatusConfigurations,
  (configurations, { phaseId, stateId }) =>
    configurations
      .filter((configuration) => configuration.isRejectedState)
      .some(({ id: configId }) => configId === phaseId || configId === stateId),
);

const getForwardSteps = (
  statesConfigurations: AssociationStatusConfiguration[],
  selectedPhaseId: string,
  moveForwardOnly: boolean = true,
): AssociationStatusConfiguration[] => {
  const startIndex = moveForwardOnly
    ? statesConfigurations.findIndex(({ id }) => selectedPhaseId === id)
    : 0;
  const subSet = statesConfigurations.slice(Math.max(0, startIndex));
  const blockIndex = subSet.findIndex(({ blockForward }) => blockForward);

  if (blockIndex >= 0) {
    return subSet.slice(0, blockIndex);
  }
  return subSet;
};

export const getNextAvailablePhases = createSelector(
  getAssociationStatusConfigurationsShortList,
  (statesConfigurations, { selectedPhaseId }) =>
    getForwardSteps(statesConfigurations, selectedPhaseId),
);

export const getPreviousAvailablePhases = createSelector(
  getAssociationStatusConfigurationsShortList,
  (statesConfigurations, { selectedPhaseId }) => {
    const startIndex = statesConfigurations.findIndex(({ id }) => selectedPhaseId === id);
    return statesConfigurations.slice(0, startIndex);
  },
);

export const getAssociationPhase = createSelector(
  getAssociationStatusConfigurations,
  (statesConfigurations, { configurationStateId }) => {
    const phaseIndex = getAssociationPhaseIndex(configurationStateId, statesConfigurations);
    return {
      phaseIndex,
      phaseConfig: statesConfigurations[phaseIndex],
    };
  },
);

export const getAssociationState = createSelector(
  getAssociationStatusConfigurations,
  (statusConfigurations, { configurationStateId }) =>
    statusConfigurations
      .flatMap((config) => config.children || [])
      .find((state) => state.id === configurationStateId),
);

export const getAssociationPhaseStates = createSelector(
  getAssociationPhase,
  ({ phaseConfig }) => phaseConfig?.children || [],
);

const getRejectedStates = createSelector(getAssociationPhaseStates, (states) =>
  states.filter((state) => state.isRejectedState),
);

export const getAssociationRejectedConfigurations = createSelector(
  getRejectedStates,
  (states) => states,
);

export const isShortListed = createSelector(
  getAssociationStatusConfigurations,
  (configurations, { phaseId, stateId }) => {
    const phase = configurations.find((config) => config.id === phaseId);
    return (
      !!phase &&
      (phase.isShortListState ||
        phase.children.find((config) => config.id === stateId)?.isShortListState)
    );
  },
);

export const getRejectionAvailableStepsConfig = createSelector(
  getAssociationStatusConfigurations,
  loadLicenseExtensions,
  (configurations, extensions, { phaseId, stateId }) => {
    const { initialPhase, initialState } = { initialPhase: phaseId, initialState: stateId };
    const selectedConfig = configurations.find((config) => config.id === initialPhase);
    const step = {
      ...selectedConfig,
      children: selectedConfig.children.filter((state) => state.isRejectedState),
    };
    return {
      initialPhase,
      initialState,
      availableSteps: [step],
    };
  },
);

export const getAssociationPhaseStatesByTypesFactory = createSelector(
  getAssociationPhaseStates,
  (states: AssociationStatusConfiguration[], { type }) =>
    states.filter((state) => state.type === type),
);

export const getMoveForwardPhases = createSelector(
  getInitialPhase,
  getAssociationStatusConfigurationsShortList,
  (
    initialPhase,
    availableSteps,
    { moveForwardOnly }: Partial<AssociationConfigurationSelectorOptions>,
  ) => [initialPhase, getForwardSteps(availableSteps, initialPhase.status, moveForwardOnly)],
);

export const getAssociationStepConfig = createSelector(
  getAssociationStatusConfigurations,
  (statesConfigurations, { configurationStateId }) => {
    const phaseIndex = getAssociationPhaseIndex(configurationStateId, statesConfigurations);
    const phaseConfig = statesConfigurations[phaseIndex];
    if (phaseConfig?.id === configurationStateId) {
      return {
        phaseIndex,
        phaseConfig,
        config: phaseConfig,
      };
    }

    const stateIndex = phaseConfig?.children?.findIndex(({ id }) => id === configurationStateId);
    const stateConfig = phaseConfig?.children[stateIndex];

    return {
      phaseIndex,
      stateIndex,
      phaseConfig,
      stateConfig,
      config: stateConfig,
    };
  },
);

export const getAllJobPageCandidatesIds = createSelector(
  getAssignsCandidatesState,
  getAssignsRejectedCandidatesPagination,
  getJobPageCandidatesState,
  (shortlistedCandidates, rejectedCandidates, totalMatchesCandidates) => {
    const shortlistedCandidatesTemp = !!shortlistedCandidates.content
      ? shortlistedCandidates.content.map(({ id }) => id)
      : [];
    const rejectedCandidatesTemp = !!rejectedCandidates
      ? rejectedCandidates.content.map(({ id }) => id)
      : [];
    const totalMatchesCandidatesTemp = !!totalMatchesCandidates?.content
      ? totalMatchesCandidates?.content.map(({ id }) => id)
      : [];
    return shortlistedCandidatesTemp
      .concat(rejectedCandidatesTemp)
      .concat(totalMatchesCandidatesTemp);
  },
);
